import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getProfile, updateProfile, createPresignedUpload } from '../api/endpoints';
import { useAuthStore } from '../store/auth';
import { useRole } from '../hooks/useRole';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { ExternalLink } from 'lucide-react';

export function ProfilePage() {
  useDocumentTitle('Mein Profil');
  const user = useAuthStore((s) => s.user);
  const { isAdmin: _isAdmin } = useRole();
  const queryClient = useQueryClient();
  const [saved, setSaved] = useState(false);

  const profileQuery = useQuery({
    queryKey: ['profile'],
    queryFn: getProfile,
  });

  const [displayName, setDisplayName] = useState('');
  const [avatarKey, setAvatarKey] = useState('');
  const [initialized, setInitialized] = useState(false);

  if (profileQuery.data && !initialized) {
    setDisplayName(profileQuery.data.displayName);
    setAvatarKey(profileQuery.data.avatarKey);
    setInitialized(true);
  }

  const updateMutation = useMutation({
    mutationFn: () => updateProfile({ displayName, avatarKey }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    },
  });

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      alert('Avatar darf maximal 2 MB groß sein');
      return;
    }
    try {
      const { uploadUrl, fileKey } = await createPresignedUpload(file.name, file.type, 'avatars');
      await fetch(uploadUrl, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } });
      setAvatarKey(fileKey);
    } catch {
      alert('Upload fehlgeschlagen');
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateMutation.mutate();
  };

  const roleLabels: Record<string, string> = {
    admin: 'Administrator',
    editor: 'Editor',
    viewer: 'Betrachter',
  };
  const roleLabel = user?.groups?.map((g) => roleLabels[g] ?? g).join(', ') ?? 'Keine Rolle';

  if (profileQuery.isLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Mein Profil</h1>
        <p className="text-gray-500 mt-1">Profil und Anzeigename bearbeiten</p>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 space-y-6">
        {/* Avatar */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Profilbild</label>
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 rounded-full bg-gray-100 border-2 border-gray-200 flex items-center justify-center overflow-hidden">
              {avatarKey ? (
                <img
                  src={`${import.meta.env.VITE_ASSETS_URL}/${avatarKey}`}
                  alt="Avatar"
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="text-2xl font-bold text-gray-400">
                  {(displayName || user?.email || '?')[0]?.toUpperCase()}
                </span>
              )}
            </div>
            <label className="cursor-pointer bg-white border border-gray-300 text-gray-700 rounded-lg px-4 py-2 text-sm font-medium hover:bg-gray-50 transition-colors">
              Bild ändern
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={handleAvatarUpload}
                className="hidden"
              />
            </label>
          </div>
        </div>

        {/* Display Name */}
        <div>
          <label htmlFor="displayName" className="block text-sm font-medium text-gray-700 mb-1">
            Anzeigename
          </label>
          <input
            id="displayName"
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Dein Name oder Alias"
            className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-gray-900 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 placeholder:text-gray-400"
          />
        </div>

        {/* Team & Einrichtung */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Team</label>
            <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 text-sm">
              {profileQuery.data?.team ? (
                profileQuery.data.team.url ? (
                  <a
                    href={profileQuery.data.team.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-indigo-600 hover:text-indigo-700 font-medium inline-flex items-center gap-1.5"
                  >
                    {profileQuery.data.team.name}
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                ) : (
                  <span className="text-gray-900">{profileQuery.data.team.name}</span>
                )
              ) : (
                <span className="text-gray-400">Kein Team zugewiesen</span>
              )}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Einrichtung</label>
            <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 text-sm">
              {profileQuery.data?.einrichtung ? (
                profileQuery.data.einrichtung.url ? (
                  <a
                    href={profileQuery.data.einrichtung.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-indigo-600 hover:text-indigo-700 font-medium inline-flex items-center gap-1.5"
                  >
                    {profileQuery.data.einrichtung.name}
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                ) : (
                  <span className="text-gray-900">{profileQuery.data.einrichtung.name}</span>
                )
              ) : (
                <span className="text-gray-400">Keine Einrichtung</span>
              )}
            </div>
          </div>
        </div>
        <p className="text-xs text-gray-400 -mt-3">Team und Einrichtung werden von Administratoren zugewiesen</p>

        {/* Read-only info */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">E-Mail</label>
            <p className="text-gray-900 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 text-sm">
              {user?.email}
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Rolle</label>
            <p className="text-gray-900 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 text-sm">
              {roleLabel}
            </p>
          </div>
        </div>

        {/* Submit */}
        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={updateMutation.isPending}
            className="bg-indigo-600 text-white px-5 py-2.5 rounded-lg font-semibold hover:bg-indigo-700 disabled:opacity-50 transition-colors shadow-sm"
          >
            {updateMutation.isPending ? 'Wird gespeichert…' : 'Profil speichern'}
          </button>
          {saved && (
            <span className="text-emerald-600 text-sm font-medium">Gespeichert!</span>
          )}
        </div>
      </form>

      {/* Zustimmungen */}
      {(profileQuery.data?.tosAcceptedAt || (profileQuery.data?.licenseAcknowledgments && profileQuery.data.licenseAcknowledgments.length > 0)) && (
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-3">Zustimmungen</h2>
          <div className="space-y-2">
            {profileQuery.data.tosAcceptedAt && (
              <div className="flex items-center justify-between bg-gray-50 rounded-lg px-4 py-3 border border-gray-200">
                <span className="text-sm font-medium text-gray-900">AGB akzeptiert</span>
                <span className="text-sm text-gray-600">
                  {new Date(profileQuery.data.tosAcceptedAt).toLocaleDateString('de-DE')}
                </span>
              </div>
            )}
            {profileQuery.data?.licenseAcknowledgments?.filter(a => a.source === 'metacom').map((ack) => (
              <div key={ack.source} className="flex items-center justify-between bg-gray-50 rounded-lg px-4 py-3 border border-gray-200">
                <span className="text-sm font-medium text-gray-900">METACOM-Lizenz bestätigt</span>
                <span className="text-sm text-gray-600">
                  {new Date(ack.acknowledgedAt).toLocaleDateString('de-DE')}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
